/**
 * messengerVoiceOutbox — la file d'envoi des vocaux de la messagerie de l'élève.
 *
 * Une seule file pour l'app (le volet de « Messages » et `messenger` poussé par-dessus lisent la
 * même) : la file du moteur (`engine/voice/voiceOutbox`), à qui la messagerie donne son envoi. Il est
 * celui de `CommonChat.onAudioSend` : `POST api/messenger/threads/{id}/audio`, en multipart, la pièce
 * sous `audio` et `temporary_id`, sans passer par S3. Deux écarts voulus : le fichier porte son vrai
 * type et son extension (l'ancien envoyait tout en `audio/m4a`, même un WebM de Firefox) ; sur le
 * web, la pièce est un `File` nommé (l'ancien envoyait le `Blob` brut, que le serveur nommait
 * « blob »). Le serveur devine l'extension du contenu reçu (`guessExtension`) : il accepte l'un
 * comme l'autre.
 *
 * L'envoi part du service sans passer par `apiRequest` : la file attend sa réponse (une promesse),
 * et une file qui relance un vocal au démarrage n'ouvrirait pas la boîte « Pas de connexion » du
 * réseau legacy. La réponse est la ressource du message, non enveloppée. La file garde le compte qui a
 * enregistré (`owner`) : un vocal en échec ne repart pas sous le compte d'un frère connecté depuis.
 */
import moment from "moment"
import { Platform } from "react-native"

import { messengerService } from "../../../../../../data/services/messengerService"
import { fetchResourceFromURI } from "../../../../../../data/utility/fileFromURI"
import { createVoiceOutbox, type VoiceFile, type VoiceOutboxItem } from "../../../../../../engine/voice/voiceOutbox"
import { currentAccountId } from "../../../../../../engine/shared/currentAccount"
import { engineOutboxTelemetry } from "../../../../../../engine/shared/outbox/outboxTelemetry"
import { setVoiceTelemetrySink } from "../../../../../../engine/voice/voiceTelemetry"
import { type ApiMessengerMessage } from "../../../../adapters/student/Messaging/studentConversationAdapter"

/** Où un vocal part : le fil, et le nom du fichier, fixé au moment du geste. */
export type MessengerVoiceTarget = { threadId: string; fileName: string }

export type MessengerVoiceItem = VoiceOutboxItem<MessengerVoiceTarget, ApiMessengerMessage | null>

/** `CommonChat.onAudioSend` : `Masteur_audio<nom affiché>_<date>`, à la vraie extension. */
export function messengerVoiceFileName(
    displayName: string | null | undefined,
    extension: string,
    today = moment()
): string {
    return `Masteur_audio${displayName ?? ""}_${today.format("YYYY-MM-DD")}${extension}`
}

/** La pièce du formulaire : sur mobile l'adresse que `FormData` lit, sur le web le `File` derrière. */
async function audioPart(
    file: VoiceFile,
    fileName: string
): Promise<Blob | { uri: string; name: string; type: string }> {
    if (Platform.OS !== "web") return { uri: file.uri, name: fileName, type: file.mimeType }
    const blob: Blob = await fetchResourceFromURI(file.uri)
    return new File([blob], fileName, { type: file.mimeType })
}

/** Le formulaire d'un vocal (`CommonChat.onAudioSend`) : la pièce sous `audio`, et `temporary_id`. */
export async function messengerVoiceForm(file: VoiceFile, fileName: string): Promise<FormData> {
    const form = new FormData()
    form.append("audio", (await audioPart(file, fileName)) as unknown as Blob)
    form.append("temporary_id", "test")
    return form
}

export async function sendMessengerVoice(item: MessengerVoiceItem): Promise<ApiMessengerMessage | null> {
    const form = await messengerVoiceForm(item.file, item.target.fileName)
    const response = (await messengerService.postAudio({ threadId: item.target.threadId }, form)) as {
        data?: ApiMessengerMessage
    }
    return response?.data ?? null
}

// La voix de l'élève parle à la télémétrie du moteur : posé une fois, avec la file qui la sert.
setVoiceTelemetrySink(engineOutboxTelemetry("voice", "student"))

export const messengerVoiceOutbox = createVoiceOutbox<MessengerVoiceTarget, ApiMessengerMessage | null>({
    storageKey: "student-messenger-voice-outbox",
    send: sendMessengerVoice,
    // L'appareil se partage : un vocal ne part, et ne se montre, que sous le compte qui l'a enregistré.
    owner: currentAccountId,
})
